import React, { useState, useEffect, useRef } from 'react';
import { useGlobalContext } from './GlobalProvider';
import { motion } from 'framer-motion';

// Define the type for the card
interface Card {
  rank: string;
  suit: string;
  rive_index: number;
  url: string;
  icon: string;
}

interface PlayerStats {
  a: number; // Attack
  s: number; // Shield
  d: number; // Damage
  m: number; // Magic
}

interface Choice {
  i: number;
  e: number;
}

// Define the type for the gameState prop
interface GameState {
  walletAddress: string;
  currentPlayer: string;
  turnCounter: number;
  startHeight: number;
  deck1: string[];
  deck2: string[];
  p1History: string[];
  p2History: string[];
  p1WonCards: string[];
  p2WonCards: string[];
  p1LastCards: string[]; // Could contain more than one card during war
  p2LastCards: string[]; // Could contain more than one card during war
  gameOver: boolean;
  roundWinner: string; // Can be "Player 1" or "Player 2"
  gameWinner: string;
  p1Choice: number;
  p2Choice: number;
  p1Stats: PlayerStats;
  p2Stats: PlayerStats;
  choices: Choice[];
}

// Define the prop type for CardDeck
interface CardDeckProps {
  gameState: GameState;
  uplift: () => void;
}



// Constant for card back texture
// Base Card: WevaVJIMls-b7tU71XtocWCfcey3FWLZbJN-sUBYZeI
// DumDumz: j845jS0fUDkn7cTkV-vJytAQw_R1NsaSz364BOQoruQ
// LK Behdeaded: g67IHqHC7_S9ZGTpZRM-W5aK-UaYw2IG1wuvf44YakE
const DECK_CARD = "oV_kP8RxlUHakz2dTNsjEBKtDIpe41coQNVKKBu2Irg";
const CARD_BACK = "j845jS0fUDkn7cTkV-vJytAQw_R1NsaSz364BOQoruQ";
const LK_CARD_BACK = "xT9zvgoxZ8dsUcLgIJ0-Nt3w7pJiKJWR7Na4r8H1yVA";

const CardDeck: React.FC<CardDeckProps> = ({ gameState , uplift }) => {

    const { activeDeck } = useGlobalContext();

    const [deck, setDeck] = useState<Card[]>([]);
     const [loading, setLoading] = useState(true);

     const audioRef = useRef<HTMLAudioElement>(null);

    const CARD_WIDTH = 162;
    const CARD_HEIGHT = 211.5;

    const Uplift = () => {
        uplift();
        console.log("From Deck");    
    };

    useEffect(() => {
        console.log("Active Deck: " + activeDeck);
      }, [ activeDeck ]);

  // Fetch the JSON deck data on component mount
  useEffect(() => {
    const fetchDeck = async () => {
      try {
        const response = await fetch('https://arweave.net/UTbCS_uO7jrM3puVwUqI9fySxzi5cMnJQNUxXXkzOUQ'); // Adjust the path to your JSON file
        const data = await response.json();
        setDeck(data.cards); // Set the deck to the data from JSON
        setLoading(false);
      } catch (error) {
        console.error('Error fetching card deck:', error);
        setLoading(false);
      }
    };

    fetchDeck();
  }, []);

  // Map gameState decks to full card data using the icon (since deck1 and deck2 are arrays of strings)
  const getFullDeckData = (deckStrings: string[]) => {
    return deckStrings
      .map(cardString => deck.find(card => card.icon === cardString)) // Match by icon
      .filter((card): card is Card => card !== undefined); // Ensure no undefined values
  };

  // Get player 1 and player 2's full deck data
  const player1Deck = getFullDeckData(gameState.deck1);
  const player2Deck = getFullDeckData(gameState.deck2);

  // Get all drawn cards for Player 1 and Player 2 (in case of war)
  const player1LastCards = getFullDeckData(gameState.p1LastCards); // All cards drawn by Player 1
  const player2LastCards = getFullDeckData(gameState.p2LastCards); // All cards drawn by Player 2

  // Get won cards
  const player1WonCards = getFullDeckData(gameState.p1WonCards);
  const player2WonCards = getFullDeckData(gameState.p2WonCards);

  if (loading) {
    return <p>Loading deck...</p>;
  }

  return (
    <>
    <audio ref={audioRef} src="./audio/Cards_Place_03.wav" />
      <div onClick={() => uplift()}  className="grid grid-cols-4 gap-10 h-[400px] pt-2 w-full bg-[#2C6A57]">
        {/* Player 1 Deck and Won Cards */}
        <div className="flex flex-col items-center w-[104px] h-auto relative mx-auto">
          {/* Deck (face down) */}
{player1Deck.map((card, index) => {
  const isLastCardInDeck = index === player1Deck.length - 1; // Check if it's the last card in the deck

  return (
    <motion.div
      key={card.rank + card.suit}
      className="absolute w-[162px] h-[211.5px] transition-transform"
      initial={{ y: index * 2, scale: 1 }}
      animate={{ y: index * 2, scale: 1 }}
      exit={{ y: 0, opacity: 0, scale: 1.5 }}
      style={{ zIndex: index }}
    >
      <img
        src={isLastCardInDeck ? `https://arweave.net/${activeDeck}` : `https://arweave.net/${DECK_CARD}` }
        alt="Card Back"
        className="w-full h-full object-cover" 
      />
    </motion.div>
  );
})}

{/* Won Cards (face up) */}
{player1WonCards.map((card, index) => {
  const isLastCardInWon = index === player1WonCards.length - 1; // Check if it's the last card in won cards

  return (
    <motion.div
      key={card.rank + card.suit}
      className="absolute w-[162px] h-[211.5px] transition-transform"
      initial={{ y: (index + player1Deck.length) * 2, scale: 1 }}
      animate={{ y: (index + player1Deck.length) * 2, scale: 1 }}
      exit={{ y: 0, opacity: 0, scale: 1.5 }}
      style={{ zIndex: index + player1Deck.length }}
    >
      <img
        src={isLastCardInWon ? `https://arweave.net/${activeDeck}` : `https://arweave.net/${DECK_CARD}` }
        alt="Card Back"
        className="w-full h-full object-cover"
      />
    </motion.div>
  );
})}

        </div>

        {/* Player 1 Drawn Cards (war cards stacked underneath the winning card) */}
        <div className="flex flex-col items-center w-full mx-auto">
          {player1LastCards.map((card, index) => (
            <motion.div
                key={card.rank + card.suit}
                className={`w-[162px] h-[211.5px] mb-[-200px] ${gameState.roundWinner === 'Player 1' && index === player1LastCards.length - 1 ? 'border-4 border-green-500 rounded-2xl' : 'rounded-2xl'}`}
                initial={{ y: 100, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: -100, opacity: 0 }}
                transition={{ duration: 0.5, ease: "easeOut" }}
                style={{ zIndex: index }}
            >
            <img src={"https://arweave.net/" + card.url} alt={`${card.rank} of ${card.suit}`} className="w-full h-full object-cover" />
          
            {/* <img src={"./images/card_shadow.png"} alt={"Card Shadow"} className="absolute inset-0 object-cover opacity-30" style={{ transform: 'translateY(10px)' }} /> */}
          
          </motion.div>
          
          ))}
        </div>

        {/* Player 2 Drawn Cards (war cards stacked underneath the winning card) */}
        <div className="flex flex-col items-center w-[216px] mx-auto">
            
          {player2LastCards.map((card, index) => (
            <motion.div
              key={card.rank + card.suit}
              className={`w-[162px] h-[211.5px] mb-[-200px] ${gameState.roundWinner === 'Player 2' && index === player2LastCards.length - 1 ? 'border-4 border-green-500 rounded-2xl' : 'rounded-2xl'}`}
              initial={{ y: -100, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: -100, opacity: 0 }}
                transition={{ duration: 0.5, ease: "easeOut" }}
              style={{ zIndex: index }}
            >
              <img src={"https://arweave.net/" + card.url} alt={`${card.rank} of ${card.suit}`} className="w-full h-full object-cover" />
            </motion.div>
          ))}
        </div>

        {/* Player 2 Deck and Won Cards */}
        <div className="flex flex-col items-center w-[104px] h-auto relative mx-auto">

            {/* Player 2 Deck (face down) */}
            {player2Deck.map((card, index) => {
            const isLastCardInDeck = index === player2Deck.length - 1; // Check if it's the last card in the deck

                return (
                    <motion.div
                    key={card.rank + card.suit}
                    className="absolute w-[162px] h-[211.5px] transition-transform"
                    initial={{ y: index * 2, scale: 1 }}
                    animate={{ y: index * 2, scale: 1 }}
                    exit={{ y: 0, opacity: 0, scale: 1.5 }}
                    style={{ zIndex: index }}
                    >
                    <img
                        src={isLastCardInDeck ? `https://arweave.net/${LK_CARD_BACK}` : `https://arweave.net/${DECK_CARD}` }
                        alt="Card Back"
                        className="w-full h-full object-cover"
                    />
                    </motion.div>
                );
            })}

            {/* Player 2 Won Cards (face up) */}
            {player2WonCards.map((card, index) => {
            const isLastCardInWon = index === player2WonCards.length - 1; // Check if it's the last card in won cards

            return (
                <motion.div
                key={card.rank + card.suit}
                className="absolute w-[162px] h-[211.5px] transition-transform"
                initial={{ y: (index + player2Deck.length) * 2, scale: 1 }}
                animate={{ y: (index + player2Deck.length) * 2, scale: 1 }}
                exit={{ y: 0, opacity: 0, scale: 1.5 }}
                style={{ zIndex: index + player2Deck.length }}
                >
                <img
                    src={isLastCardInWon ? `https://arweave.net/${LK_CARD_BACK}` : `https://arweave.net/${DECK_CARD}` }
                    alt="Card Back"
                    className="w-full h-full object-cover"
                />
                </motion.div>
            );
            })}

        </div>
      </div>
    </>
  );
};

export default CardDeck;
